/** A cancellable read owned by one Organization and request generation. */
export type OrganizationResponseToken = {
  readonly generation: number;
  readonly organizationId: string;
  readonly signal: AbortSignal;
};

/** Owns the latest page read for one selected Organization. */
export class OrganizationResponseGuard {
  #generation = 0;
  #organizationId: string;
  #controller: AbortController | null = null;

  /** Start a read boundary for the selected Organization. */
  constructor(organizationId: string) {
    this.#organizationId = organizationId;
  }

  /** Cancel reads from the previous Organization when selection changes. */
  selectOrganization(organizationId: string): void {
    if (organizationId === this.#organizationId) return;
    this.#organizationId = organizationId;
    this.invalidate();
  }

  /** Cancel the previous read and claim the next response. */
  begin(): OrganizationResponseToken {
    this.invalidate();
    this.#controller = new AbortController();
    return {
      generation: this.#generation,
      organizationId: this.#organizationId,
      signal: this.#controller.signal,
    };
  }

  /** Revoke ownership and cancel any outstanding read. */
  invalidate(): void {
    this.#generation += 1;
    this.#controller?.abort();
  }

  /** Whether a result still belongs to this page's latest read. */
  owns(token: OrganizationResponseToken): boolean {
    return (
      token.organizationId === this.#organizationId &&
      token.generation === this.#generation
    );
  }
}
